import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { InsightsService } from '../services/insights'
import { AppState } from '../stores/app'
import { observe } from 'mobx'
import dayjs from 'dayjs'

export type InsightsDatePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days'

export interface InsightsDailyRow {
    date: string
    impressions: number
    clicks: number
    ctrDecimal: number
}

export interface InsightsTopNotification {
    notificationId: string
    title: string
    body?: string
    imageUrl?: string
    source?: string
    sendTime?: string
    audience?: { targeted?: Array<{ type: string; label: string; value: number }>; excluded?: any[] }
    impressions: number
    clicks: number
    ctrDecimal: number
}

export interface InsightsSummary {
    impressions: number
    clicks: number
    ctrDecimal: number
}

interface UseInsightsDataResult {
    summary: InsightsSummary | null
    daily: InsightsDailyRow[]
    /** The date string that should be highlighted on the chart (for single-day presets) */
    highlightDate: string | null
    topNotifications: InsightsTopNotification[]
    loading: boolean
    error: Error | null
    refresh: () => void
}

function getDateRange(preset: InsightsDatePreset): { since: string; through: string } {
    const today = dayjs().format('YYYY-MM-DD')
    switch (preset) {
        case 'today':
            // Fetch last 3 days so chart always has multiple points
            return { since: dayjs().subtract(2, 'day').format('YYYY-MM-DD'), through: today }
        case 'yesterday': {
            // Fetch 3 days ending yesterday
            return {
                since: dayjs().subtract(3, 'day').format('YYYY-MM-DD'),
                through: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            }
        }
        case 'last_7_days':
            return { since: dayjs().subtract(6, 'day').format('YYYY-MM-DD'), through: today }
        case 'last_30_days':
            return { since: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), through: today }
    }
}

/** Returns the single-day date for KPI summary calculation (Today or Yesterday) */
function getSummaryDate(preset: InsightsDatePreset): string | null {
    switch (preset) {
        case 'today':
            return dayjs().format('YYYY-MM-DD')
        case 'yesterday':
            return dayjs().subtract(1, 'day').format('YYYY-MM-DD')
        default:
            return null // use full range for summary
    }
}

/**
 * Chart always shows at least 7 days for context, even when the KPI
 * summary is scoped to a single day (Today/Yesterday).
 */
function getChartDateRange(preset: InsightsDatePreset): { since: string; through: string } {
    const today = dayjs().format('YYYY-MM-DD')
    switch (preset) {
        case 'today':
        case 'yesterday':
        case 'last_7_days':
            return { since: dayjs().subtract(6, 'day').format('YYYY-MM-DD'), through: today }
        case 'last_30_days':
            return { since: dayjs().subtract(29, 'day').format('YYYY-MM-DD'), through: today }
    }
}

/**
 * Fills in missing dates between `since` and `through` with zero-value rows.
 * Ensures the chart has a continuous x-axis without gaps.
 */
function backfillDailyRows(rows: InsightsDailyRow[], since: string, through: string): InsightsDailyRow[] {
    const rowMap = new Map<string, InsightsDailyRow>()
    for (const row of rows) {
        rowMap.set(row.date, row)
    }

    const result: InsightsDailyRow[] = []
    let current = dayjs(since)
    const end = dayjs(through)

    while (current.isBefore(end) || current.isSame(end, 'day')) {
        const dateStr = current.format('YYYY-MM-DD')
        result.push(rowMap.get(dateStr) ?? { date: dateStr, impressions: 0, clicks: 0, ctrDecimal: 0 })
        current = current.add(1, 'day')
    }

    return result
}

export function useInsightsData(preset: InsightsDatePreset): UseInsightsDataResult {
    const insightsService = useRef(Container.get(InsightsService)).current
    const appState = useRef(Container.get(AppState)).current

    const [summary, setSummary] = useState<InsightsSummary | null>(null)
    const [daily, setDaily] = useState<InsightsDailyRow[]>([])
    const [topNotifications, setTopNotifications] = useState<InsightsTopNotification[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    // For single-day presets, highlight that day on the chart
    const highlightDate = useMemo(() => {
        if (preset === 'today') return dayjs().format('YYYY-MM-DD')
        if (preset === 'yesterday') return dayjs().subtract(1, 'day').format('YYYY-MM-DD')
        return null
    }, [preset])

    const fetchData = useCallback(async () => {
        const domainId = appState.currentDomain?.id
        if (!domainId) return

        setLoading(true)
        setError(null)

        const dateRange = getDateRange(preset)
        const chartRange = getChartDateRange(preset)

        try {
            // Fetch daily breakdown + top notifications in parallel
            const [dailyResult, topResult] = await Promise.all([
                // Time-series data (daily) — always uses chart range for context
                insightsService.fetch(
                    {
                        entity: 'notifications',
                        date_increment: 'DAY',
                        date_range: chartRange,
                        breakdowns: ['domain'],
                        fields: ['impressions', 'clicks', 'ctr_decimal'],
                        filters: [{ field: 'domain.id', operator: 'eq', value: domainId }],
                        order: { field: 'send_date', direction: 'asc' },
                    } as any,
                    { cancellationKey: 'mv.insights.daily', scopedInsightsPath: 'insights' },
                ),

                // Top notifications: every notification in the window, unpaged. The view ranks them
                // by whichever metric is active and keeps 25, so a page of the top 25 by any one
                // metric would hide notifications that lead on another.
                insightsService.fetch(
                    {
                        entity: 'notifications',
                        date_increment: 'LIFETIME',
                        date_range: dateRange,
                        breakdowns: ['notification'],
                        fields: [
                            'notification.id',
                            'notification.title',
                            'notification.body',
                            'notification.image_url',
                            'notification.source',
                            'notification.scheduled_time',
                            'notification.scheduled_time_zone',
                            'notification.audiences',
                            'impressions',
                            'clicks',
                            'ctr_decimal',
                        ],
                        filters: [{ field: 'domain.id', operator: 'eq', value: domainId }],
                        order: { field: 'clicks', direction: 'desc' },
                    } as any,
                    { cancellationKey: 'mv.insights.top', scopedInsightsPath: 'insights' },
                ),
            ])

            // Process daily/hourly rows
            const rawRows: InsightsDailyRow[] = Array.isArray(dailyResult)
                ? dailyResult.map((r: any) => ({
                      date: r.send_date ?? r.send_hour ?? r.event_date ?? r.report_date ?? '',
                      impressions: r.impressions ?? 0,
                      clicks: r.clicks ?? 0,
                      ctrDecimal: r.ctr_decimal ?? 0,
                  }))
                : []

            // Backfill missing dates in the chart range with zero values
            // so the chart renders a continuous axis without gaps
            const rows = backfillDailyRows(rawRows, chartRange.since, chartRange.through)

            setDaily(rows)

            // Compute summary totals — for single-day presets, only use that day's data
            const summaryDate = getSummaryDate(preset)
            const summaryRows = summaryDate ? rows.filter((r) => r.date === summaryDate) : rows

            const totalImpressions = summaryRows.reduce((sum, r) => sum + r.impressions, 0)
            const totalClicks = summaryRows.reduce((sum, r) => sum + r.clicks, 0)
            const avgCtr = totalImpressions > 0 ? totalClicks / totalImpressions : 0

            setSummary({
                impressions: totalImpressions,
                clicks: totalClicks,
                ctrDecimal: avgCtr,
            })

            // Process top notifications
            const top: InsightsTopNotification[] = Array.isArray(topResult)
                ? topResult
                      .filter((r: any) => r.clicks > 0)
                      .map((r: any) => {
                          const notif = r.notification ?? {}
                          return {
                              notificationId: String(r['notification.id'] ?? notif.id ?? ''),
                              title: r['notification.title'] ?? notif.title ?? 'Untitled',
                              body: r['notification.body'] ?? notif.body ?? undefined,
                              imageUrl: r['notification.image_url'] ?? notif.image_url ?? undefined,
                              source: r['notification.source'] ?? notif.source ?? undefined,
                              sendTime:
                                  r['notification.scheduled_time'] ?? notif.scheduled_time ?? r.send_date ?? undefined,
                              audience: r['notification.audiences'] ?? notif.audiences ?? undefined,
                              impressions: r.impressions ?? 0,
                              clicks: r.clicks ?? 0,
                              ctrDecimal: r.ctr_decimal ?? 0,
                          }
                      })
                : []

            setTopNotifications(top)
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch insights'))
        } finally {
            setLoading(false)
        }
    }, [appState, insightsService, preset])

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            await fetchData()
        }
        run()

        const dispose = observe(appState, 'currentDomainJsonData', () => {
            // Clear stale data immediately so skeleton states show
            setSummary(null)
            setDaily([])
            setTopNotifications([])
            fetchData()
        })

        return () => {
            cancelled = true
            dispose()
        }
    }, [fetchData, appState])

    return { summary, daily, highlightDate, topNotifications, loading, error, refresh: fetchData }
}
